import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Valve, ValveUpdate, ValveCreate } from '@/types/api';
import { valveApi } from '@/services/valveApi';

interface ValveState {
  valves: Record<string, Valve>; // valve_id -> Valve
  loading: boolean;
  error: string | null;
  selectedValveId: string | null;
  dialogOpen: boolean;
  dialogLoading: boolean;
  dialogError: string | null;
}

const initialState: ValveState = {
  valves: {},
  loading: false,
  error: null,
  selectedValveId: null,
  dialogOpen: false,
  dialogLoading: false,
  dialogError: null,
};

// Async thunks
export const fetchValvesAsync = createAsyncThunk(
  'valves/fetchValves',
  async (params: { device_id?: number; enabled_only?: boolean } = {}, { rejectWithValue }) => {
    try {
      const response = await valveApi.getValves(params);
      return response.valves;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch valves');
    }
  }
);

export const fetchValveAsync = createAsyncThunk(
  'valves/fetchValve',
  async (valveId: string, { rejectWithValue }) => {
    try {
      const valve = await valveApi.getValve(valveId);
      return valve;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch valve');
    }
  }
);

export const createValveAsync = createAsyncThunk(
  'valves/createValve',
  async (valveData: ValveCreate, { rejectWithValue }) => {
    try {
      const valve = await valveApi.createValve(valveData);
      return valve;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create valve');
    }
  }
);

export const updateValveAsync = createAsyncThunk(
  'valves/updateValve',
  async ({ valveId, valveData }: { valveId: string; valveData: ValveUpdate }, { rejectWithValue }) => {
    try {
      const updatedValve = await valveApi.updateValve(valveId, valveData);
      return updatedValve;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update valve');
    }
  }
);

export const deleteValveAsync = createAsyncThunk(
  'valves/deleteValve',
  async (valveId: string, { rejectWithValue }) => {
    try {
      await valveApi.deleteValve(valveId);
      return valveId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete valve');
    }
  }
);

const valveSlice = createSlice({
  name: 'valves',
  initialState,
  reducers: {
    openValveSettingsDialog: (state, action: PayloadAction<string>) => {
      state.selectedValveId = action.payload;
      state.dialogOpen = true;
      state.dialogError = null;
    },
    closeValveSettingsDialog: (state) => {
      state.dialogOpen = false;
      state.selectedValveId = null;
      state.dialogError = null;
    },
    clearValveError: (state) => {
      state.error = null;
    },
    clearDialogError: (state) => {
      state.dialogError = null;
    },
    setSelectedValve: (state, action: PayloadAction<string | null>) => {
      state.selectedValveId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch valves
      .addCase(fetchValvesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValvesAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Convert array to object with ID as key
        const valvesObject: Record<string, Valve> = {};
        action.payload.forEach((valve: Valve) => {
          valvesObject[valve.id] = valve;
        });
        state.valves = valvesObject;
      })
      .addCase(fetchValvesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch single valve
      .addCase(fetchValveAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValveAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.valves[action.payload.id] = action.payload;
      })
      .addCase(fetchValveAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create valve
      .addCase(createValveAsync.pending, (state) => {
        state.dialogLoading = true;
        state.dialogError = null;
      })
      .addCase(createValveAsync.fulfilled, (state, action) => {
        state.dialogLoading = false;
        state.valves[action.payload.id] = action.payload;
        state.dialogOpen = false;
      })
      .addCase(createValveAsync.rejected, (state, action) => {
        state.dialogLoading = false;
        state.dialogError = action.payload as string;
      })
      
      // Update valve
      .addCase(updateValveAsync.pending, (state) => {
        state.dialogLoading = true;
        state.dialogError = null;
      })
      .addCase(updateValveAsync.fulfilled, (state, action) => {
        state.dialogLoading = false;
        state.valves[action.payload.id] = action.payload;
        state.dialogOpen = false;
      })
      .addCase(updateValveAsync.rejected, (state, action) => {
        state.dialogLoading = false;
        state.dialogError = action.payload as string;
      })
      
      // Delete valve
      .addCase(deleteValveAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteValveAsync.fulfilled, (state, action) => {
        state.loading = false;
        delete state.valves[action.payload];
      })
      .addCase(deleteValveAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

// Selectors
export const selectValves = (state: { valves: ValveState }) => state.valves.valves;
export const selectValve = (valveId: string) => (state: { valves: ValveState }) => 
  state.valves.valves[valveId];
export const selectValvesArray = (state: { valves: ValveState }) => 
  Object.values(state.valves.valves);
export const selectValvesLoading = (state: { valves: ValveState }) => state.valves.loading;
export const selectValvesError = (state: { valves: ValveState }) => state.valves.error;
export const selectSelectedValve = (state: { valves: ValveState }) => 
  state.valves.selectedValveId ? state.valves.valves[state.valves.selectedValveId] : null;
export const selectValveDialogOpen = (state: { valves: ValveState }) => state.valves.dialogOpen;
export const selectValveDialogLoading = (state: { valves: ValveState }) => state.valves.dialogLoading;
export const selectValveDialogError = (state: { valves: ValveState }) => state.valves.dialogError;

export const {
  openValveSettingsDialog,
  closeValveSettingsDialog,
  clearValveError,
  clearDialogError,
  setSelectedValve,
} = valveSlice.actions;

export default valveSlice.reducer;